/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {Observable} from 'rxjs';
import {Cacheable} from '../../decorators/cacheable.decorator';
import {SettlementStatusBaseContract} from './contracts/settlement-status-base.contract';
import {SettlementResultContract} from './contracts/settlement-result.contract';
import {SettlementQueryContract} from './contracts/settlement-query.contract';
import {SupplierBaseContract} from './contracts/supplier-base.contract';
import {SettlementRejectCommandContract} from './contracts/settlement-reject-command.contract';
import {SettlementAcceptCommandContract} from './contracts/settlement-accept-command.contract';
import {SettlementDetailContract} from './contracts/settlement-detail.contract';
import {CustomerBaseContract} from './contracts/customer-base.contract';

@Injectable({
    providedIn: 'root'
})
export class SettlementsApi {

    private settlementsBaseUrl = `${environment.settlementsBaseUrl}/api/settlement`;
    private settlementsSupplierUrl = `${environment.settlementsBaseUrl}/api/supplier/base`;
    private settlementsCustomerUrl = `${environment.settlementsBaseUrl}/api/customer/base`;
    private settlementSearch = 'search';
    private settlementDetail = 'detail';
    private settlementTodo = 'toDo';

    private httpOptions = {
        headers: new HttpHeaders({
            'accept': 'application/json',
            'Content-Type': 'application/json'
        })
    };

    constructor(private http: HttpClient) {
    }

    /**
     * Gets Settlements from the BE
     * @param settlementsQuery the search query criteria
     * @param businessConfiguration the settlement business configuration (Customer/Supplier)
     */
    getSettlements = (settlementsQuery: SettlementQueryContract, businessConfiguration: string): Observable<SettlementResultContract> =>
            this.http.post<SettlementResultContract>(`${this.settlementsBaseUrl}/${businessConfiguration}/${this.settlementSearch}`,
                    settlementsQuery, this.httpOptions);

    /**
     * This method retrieves the settlement status
     */
    @Cacheable()
    getAllSettlementStatusBase(): Observable<SettlementStatusBaseContract[]> {
        return this.http.get<SettlementStatusBaseContract[]>(`${this.settlementsBaseUrl}/status/base`, this.httpOptions);
    }

    /**
     * This method retrieves the supplier base
     */
    @Cacheable()
    getAllSettlementSupplierBase(): Observable<SupplierBaseContract[]> {
        return this.http.get<SupplierBaseContract[]>(this.settlementsSupplierUrl, this.httpOptions);
    }

    /**
     * This method retrieves the customer base
     */
    @Cacheable()
    getAllSettlementCustomerBase(): Observable<CustomerBaseContract[]> {
        return this.http.get<CustomerBaseContract[]>(this.settlementsCustomerUrl, this.httpOptions);
    }

    /**
     * This method retrieves the settlement
     * @param params the http params
     * @param businessConfiguration the settlement business configuration (Customer/Supplier)
     */
    getSettlement(params: HttpParams,
                  businessConfiguration: string): Observable<SettlementDetailContract> {
        return this.http.get<SettlementDetailContract>(`${this.settlementsBaseUrl}/${businessConfiguration}/${this.settlementDetail}`,
                {...this.httpOptions, params});
    }

    /**
     * This method rejects a settlement
     * @param params the http params
     * @param settlementCommandReject the settlement command reject
     * @param businessConfiguration the settlement business configuration (Customer/Supplier)
     */
    rejectSettlement(params: HttpParams,
                     settlementCommandReject: SettlementRejectCommandContract, businessConfiguration: string): Observable<void> {
        return this.http.put<void>(`${this.settlementsBaseUrl}/${businessConfiguration}/reject`,
                settlementCommandReject, {...this.httpOptions, params});
    }

    /**
     * This method accepts a settlement
     * @param params the http params
     * @param settlementCommandAccept the settlement command accept
     * @param businessConfiguration the settlement business configuration (Customer/Supplier)
     */
    acceptSettlement(params: HttpParams,
                     settlementCommandAccept: SettlementAcceptCommandContract, businessConfiguration: string): Observable<void> {
        return this.http.put<void>(`${this.settlementsBaseUrl}/${businessConfiguration}/accept`,
                settlementCommandAccept, {...this.httpOptions, params});
    }

    /**
     * Gets SettlementsTodo from the BE
     * @param SettlementsQuery the search query criteria
     * @param businessConfiguration the settlement business configuration (Customer/Supplier)
     */
    getSettlementsToDo = (SettlementsQuery: SettlementQueryContract, businessConfiguration: string): Observable<SettlementResultContract> =>
            this.http.post<SettlementResultContract>(`${this.settlementsBaseUrl}/${businessConfiguration}/${this.settlementTodo}`,
                    SettlementsQuery, this.httpOptions);

    /**
     * Returns SettlementsTodo count from the BE
     * @param businessConfiguration the settlement business configuration (Customer/Supplier)
     */
    getSettlementsToDoCount(businessConfiguration: string): Observable<number> {
        return this.http.get<number>(`${this.settlementsBaseUrl}/${businessConfiguration}/${this.settlementTodo}/count`, this.httpOptions);
    }
}
